import { SilverbackPageContext } from '@amazeelabs/gatsby-source-silverback';
import {
  buildForm,
  buildLink,
  buildUrl,
  ContentRoute,
  RouteSlotInput,
} from '@caritas/ui';
import { useState } from 'react';
import { createStore } from 'zustand';

import {
  ContentHubResult,
  useContentHubQuery,
} from '../../../generated/queries/react-queries';
import { useContentHubFilter } from '../../commons/content-hub';
import { useQueryParams } from '../../commons/query-string';
import { isProject } from '../../utils/is-project';
import { languageFallback } from '../../utils/localizations';
import { mapImage } from '../Image.mappers';
import { hasImage } from './ContentPageHeader.mappers';

type ContentHubResults = {
  data: { contentHub: ContentHubResult };
};

type SelectWidgetsOpenState = {
  openedWidgets: string[];
  update: (widgetName: string, widgetOpen: boolean) => void;
};
const selectWidgetsOpenStateStore = createStore<SelectWidgetsOpenState>(
  (set) => ({
    openedWidgets: [],
    update: (widgetName: string, widgetOpen: boolean) => {
      set(state => {
        // Reset the state if the widgetOpen flag is false, and there is no
        // widgetName. This means all the widgets should be closed.
        if (widgetName === '' && !widgetOpen) {
          return {
            openedWidgets: [],
          }
        }
        if (widgetOpen) {
          return {
            openedWidgets: [widgetName]
          }
        }
        return {
          openedWidgets: state.openedWidgets.filter(item => widgetName !== item),
        }
      });
    },
  }),
);

export const mapContentHub = (pageContext: SilverbackPageContext) =>
  function (
    input: BlockContentHubFragment,
  ): RouteSlotInput<typeof ContentRoute, 'body', 'contentHub'> {
    return {
      key: 'contentHub',
      input: function useContentHub() {
        const currentGermanPath = pageContext.localizations?.reduce((accumulator, currentValue) => {
          if (currentValue.locale === 'de') {
            return currentValue;
          }
          return accumulator;
        });
        const urlQuery = useQueryParams();
        const GermanLink = buildLink({
          href: buildUrl([currentGermanPath?.path], urlQuery),
        });

        const preFilteredValues = {
          types: input?.formats?.length
            ? (input.formats as string[])
            : undefined,
          countries: input?.countries?.length
            ? (input.countries as string[])
            : undefined,
          topics: input?.topics?.length
            ? (input.topics as string[])
            : undefined,
        };
        const {
          contentHubResults,
          setFilters,
          currentValues,
          pager,
          resultsAreLoading,
        } = useContentHubFilter(
          pageContext,
          input.itemsPerPage,
          preFilteredValues,
        );

        const [selectedFilters, setSelectedFilters] = useState(currentValues);
        const initialValues = {
          types: selectedFilters.types || currentValues.types,
          themes: selectedFilters.themes || currentValues.themes,
          countries: selectedFilters.countries || currentValues.countries,
        };

        const contentHubEstimatedResults = useContentHubQuery({
          input: {
            langcode: languageFallback(pageContext.locale || 'de'),
            filters: {
              topics: initialValues.themes || [],
              types: initialValues.types || [],
              countries: initialValues.countries || [],
            },
            pagination: {
              offset: 0,
              limit: 10000,
            },
            skipResults: true,
          },
        });
        return [
          {
            isLoading:
              resultsAreLoading || contentHubEstimatedResults.isLoading,
            GermanLink: GermanLink,
            displayFilters: true,
            formFilters: {
              Form: buildForm({
                initialValues,
                onChange: (values) => {
                  if (
                    JSON.stringify(values) !== JSON.stringify(selectedFilters)
                  ) {
                    setSelectedFilters(values);

                  }
                },
                onSubmit: (values) => {
                  setFilters(JSON.stringify(values));
                  // Whenever we submit the form, because the filters changed, we
                  // want to reset the current page, otherwise we could request
                  // a non-existent page.
                  pager?.setCurrentPage('0');
                },
              }),
              themes: input?.hideFilters?.includes('topic')
                ? undefined
                : contentHubEstimatedResults.data?.contentHub?.facets?.topics ||
                  [],
              countries: input?.hideFilters?.includes('country')
                ? undefined
                : contentHubEstimatedResults.data?.contentHub?.facets
                    ?.country || [],
              types: input?.hideFilters?.includes('format')
                ? undefined
                : contentHubEstimatedResults.data?.contentHub?.facets?.format ||
                  [],
              count: contentHubEstimatedResults?.data?.contentHub.total,
              selectWidgetsOpenStateStore: selectWidgetsOpenStateStore,
            },
            teasers: {
              teasers: mapTeasers(
                getTeasers(contentHubResults as unknown as ContentHubResults),
              ),
            },
            pagerLinks: pager && {
              FirstPage: pager.firstPage,
              PreviousPage: pager.previousPage,
              NextPage: pager.nextPage,
              LastPage: pager.lastPage,
              pages: pager.pages,
              hasMissingLeftPages: pager.hasMissingLeftPages,
              hasMissingRightPages: pager.hasMissingRightPages,
            },
          },
          200,
        ];
      },
    };
  };

const getTeasers = (dynamicTeasersResult: ContentHubResults): Teaser[] => {
  const dynamicTeasers = dynamicTeasersResult.data
    ? dynamicTeasersResult.data.contentHub.results.map(
        (contentPage) => contentPage.teaser,
      )
    : [];

  return dynamicTeasers as Teaser[];
};

const mapTeasers = (teasers: Teaser[]) => {
  return (
    teasers
      // Filter out potential nulls first
      ?.filter((teaser) => teaser !== null)
      .map((item) => {
        // We know this won't be null, so force typing here
        const teaser = item as unknown as Teaser;
        const image = hasImage(teaser.image)
          ? mapImage({
              image: teaser.image.image,
              preset: 'teaser',
              sizes: '(max-width: 1023px) 900px, 300px',
              caption: teaser.image?.caption,
            })
          : undefined;
        return {
          title: teaser.title,
          Link: buildLink({ href: teaser.url }),
          Image: image,
          date: teaser.date,
          language: teaser.language,
          description: item?.description,
          sosTag: item?.headerDisplayMode === 'disaster_relief',
          status: item.status,
          isProject: isProject(item.format),
        };
      }) || []
  );
};
